import type { BalanceType } from '../../../api/fetchers/userInfo/types';
import { BetStateBitMask, updateBetStateBitMask } from '../../../logger/BetStateBitMask';
import { Logger } from '../../../logger/Logger';
import { autoplayActions } from '../../../store/slices/autoplay/slice';
import type { AutoplaySliceType } from '../../../store/slices/autoplay/types';
import type { BetsSliceType, IBetLimits, IBetSpots } from '../../../store/slices/bets/types';
import { toastActions } from '../../../store/slices/toast/slice';
import { countTotalBet, getMinimalBet, isFreeBetPlaced } from '../../../utils/bets';

import { BaseCoreGameCommunicationProcessor } from './BaseCoreGameCommunicationProcessor';

export abstract class CoreLowBalanceGameCommunicationProcessor extends BaseCoreGameCommunicationProcessor {
  public checkLowBalanceAndUpdateState(
    balance?: BalanceType,
    freeChipBalance?: number,
    betLimits: IBetLimits | null = null
  ): boolean {
    const minBetSize = getMinimalBet(betLimits);
    const currBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
    const isLowBalance = currBalance < minBetSize && (freeChipBalance ?? 0) < minBetSize;

    if (isLowBalance) {
      updateBetStateBitMask(BetStateBitMask.InsufficientFunds);
    }
    return isLowBalance;
  }

  public checkAutoplayAvailability(
    balance: BalanceType | undefined,
    bets: BetsSliceType,
    autoplay: AutoplaySliceType,
    freeChipBalance?: number
  ): void {
    if (autoplay.isInProgress) {
      const totalBet = this.getTotalBet(autoplay.savedBet?.betSpots);

      const savedBetSpots = Object.keys(autoplay.savedBet?.betSpots ?? {}).filter(
        (el) => (autoplay.savedBet?.betSpots?.[el]?.value ?? 0) > 0
      );
      const allBetsUnavailable = savedBetSpots.every((r) => bets.disabledBetSpots.includes(r));

      if (allBetsUnavailable) {
        this.store.dispatch(autoplayActions.stop());
        this.store.dispatch(toastActions.createToast('AUTOPLAY_PREFERENCES_STOPPED'));
        Logger.logEvent('autoplay_stop', {
          additional: 'BetsUnavailable',
        });
      }

      const casinoBalance = (balance?.amount ?? 0) + (balance?.bonus ?? 0);
      const isFreeBet = isFreeBetPlaced(autoplay.savedBet?.betSpots);
      const currBalance = isFreeBet ? freeChipBalance! : casinoBalance;
      if (totalBet > currBalance) {
        this.store.dispatch(autoplayActions.stop());
        this.store.dispatch(toastActions.createToast('AUTOPLAY_SUPPORT_CURR_BAL'));
        Logger.logEvent('autoplay_stop', {
          additional: 'NoMoney',
        });
      }
    }
  }

  private getTotalBet(betSpots?: IBetSpots): number {
    const digits = this.store.getState().gameState.currencyInfo?.digitsNumber ?? 0;
    return countTotalBet(betSpots ?? {}, digits);
  }
}
